import axios from 'axios';
import { CFG } from '../config/env';

// LocalStorage keys for token management
const LS_KEYS = {
  jwt: 'nkbb_jwt',
  uid: 'nkbb_uid',
  name: 'nkbb_name',
  avatar: 'nkbb_avatar',
  idToken: 'nkbb_idt'
};

let isRefreshing = false;
let refreshSubscribers = [];

// Public client (no auth needed)
export const publicClient = axios.create({
  baseURL: CFG.API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Authenticated client (requires JWT)
export const authClient = axios.create({
  baseURL: CFG.API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
authClient.interceptors.request.use((config) => {
  const jwt = localStorage.getItem(LS_KEYS.jwt);
  if (jwt) {
    config.headers.Authorization = `Bearer ${jwt}`;
  }
  return config;
});

// Handle 401 responses with re-verification
authClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue this request to retry after refresh
        return new Promise((resolve) => {
          refreshSubscribers.push(() => resolve(authClient(originalRequest)));
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to re-verify with stored ID token first
        const storedIdToken = localStorage.getItem(LS_KEYS.idToken);
        
        if (storedIdToken) {
          console.log('Re-verifying with stored ID token');
          const response = await publicClient.post('/auth/line/verify', {
            id_token: storedIdToken
          });

          const { access_token, user_id, name, avatar } = response.data;
          
          // Update stored tokens
          localStorage.setItem(LS_KEYS.jwt, access_token);
          localStorage.setItem(LS_KEYS.uid, String(user_id));
          localStorage.setItem(LS_KEYS.name, name || '');
          localStorage.setItem(LS_KEYS.avatar, avatar || '');

          // Process queued requests
          refreshSubscribers.forEach(callback => callback());
          refreshSubscribers = [];
          isRefreshing = false;

          // Retry original request
          return authClient(originalRequest);
        } else {
          throw new Error('No stored ID token for re-verification');
        }
      } catch (refreshError) {
        console.error('Re-verification failed:', refreshError);
        
        // Clear all auth data and redirect to login
        clearAuthData();
        isRefreshing = false;
        refreshSubscribers = [];
        
        // Redirect to root for re-authentication
        if (window.location.pathname !== '/') {
          window.location.href = '/';
        }
        
        throw refreshError;
      }
    }

    return Promise.reject(error);
  }
);

// Utility functions for token management
export const getStoredToken = () => localStorage.getItem(LS_KEYS.jwt);
export const getStoredUserId = () => localStorage.getItem(LS_KEYS.uid);
export const getStoredUserName = () => localStorage.getItem(LS_KEYS.name);
export const getStoredUserAvatar = () => localStorage.getItem(LS_KEYS.avatar);
export const getStoredIdToken = () => localStorage.getItem(LS_KEYS.idToken);

export const setAuthData = (data) => {
  localStorage.setItem(LS_KEYS.jwt, data.access_token);
  localStorage.setItem(LS_KEYS.uid, String(data.user_id));
  localStorage.setItem(LS_KEYS.name, data.name || '');
  localStorage.setItem(LS_KEYS.avatar, data.avatar || '');
  if (data.id_token) {
    localStorage.setItem(LS_KEYS.idToken, data.id_token);
  }
};

export const clearAuthData = () => {
  Object.values(LS_KEYS).forEach(key => {
    localStorage.removeItem(key);
  });
};

// Verify LINE authentication
export const verifyLineAuth = async (idToken) => {
  const response = await publicClient.post('/auth/line/verify', {
    id_token: idToken
  });

  const authData = { ...response.data, id_token: idToken };
  setAuthData(authData);
  
  return authData;
};

// API methods
export const apiMethods = {
  // Public endpoints
  health: () => publicClient.get('/health'),
  leaderboard: (params = {}) => {
    const { signal, ...requestParams } = params;
    return publicClient.get('/leaderboard', { 
      params: requestParams,
      ...(signal && { signal })
    });
  },

  // Protected endpoints  
  getPlayerOverview: (userId) => authClient.get(`/players/${userId}/overview`),
  getPlayerEloProgress: (userId, params = {}) => 
    authClient.get(`/players/${userId}/elo-progress`, { params }),
  getPlayerMatches: (userId, params = {}) =>
    authClient.get(`/players/${userId}/matches`, { params }),
};